/**
 * Helpers partagés — module Paie (Batch #14.250).
 */
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";
import { fmtCAD, today, addDays } from "@/lib/format";
export { API, authH, fmtCAD, today, addDays };

export const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300";
export const cellCls = "border border-slate-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:bg-slate-50 disabled:text-slate-500";
export const YEARS = [0, -1, -2, -3].map((d) => new Date().getFullYear() + d);
export const MONTHS_FR = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
export const MONTHS_LONG = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export const FREQ_LABELS = { weekly: "Hebdomadaire", biweekly: "Aux 2 semaines", monthly: "Mensuelle" };
export const FREQ_DAYS = { weekly: 7, biweekly: 14, monthly: 0 };
export const PAY_TYPES = { hourly: "Horaire", salary: "Salaire annuel" };
export const PAYMENT_METHODS = { deposit: "Dépôt direct", cheque: "Chèque" };
export const DELIVERY_MODES = { manual: "Téléchargement seulement (aucun envoi automatique)", auto_employee: "Envoi automatique à chaque employé lorsque la période est marquée payée", batch_one: "Envoi groupé de tous les bulletins à une seule adresse" };
export const RUN_STATUS = {
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  calculated: { label: "Calculée", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  approved: { label: "Approuvée", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  paid: { label: "Payée", cls: "bg-orange-100 text-orange-800 border-orange-200" },
};
export const REMIT_STATUS = {
  none: { label: "—", cls: "bg-slate-50 text-slate-400 border-slate-200" },
  upcoming: { label: "À venir", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  due_soon: { label: "Échéance proche", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  overdue: { label: "En retard", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  paid: { label: "Remise faite", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export function StatusBadge({ status, map = RUN_STATUS, testId }) {
  const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${s.cls}`} data-testid={testId}>{s.label}</span>;
}

/** Téléchargement authentifié (Bearer) → blob → lien. */
export async function downloadFile(path, filename, setBusy) {
  setBusy?.(true);
  try {
    const r = await axios.get(`${API}${path}`, { headers: authH(), responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(`${filename} téléchargé`);
  } catch (e) {
    let msg = "Erreur de téléchargement";
    try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* blob non JSON */ }
    toast.error(msg);
  } finally {
    setBusy?.(false);
  }
}

export function YearSelect({ value, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))}
      className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-orange-300" data-testid={testId}>
      {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
  );
}

const KPI_TONES = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  rose: "border-rose-200 bg-rose-50 text-rose-800",
  sky: "border-sky-200 bg-sky-50 text-sky-800",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  orange: "border-orange-200 bg-orange-50 text-orange-800",
  slate: "border-slate-200 bg-slate-50 text-slate-800",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
};

export function KpiCard({ label, value, sub, tone = "orange", icon: Icon, testId }) {
  return (
    <div className={`rounded-xl border-2 p-4 ${KPI_TONES[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-50" />}
      </div>
      <div className="text-lg font-bold font-mono">{value}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

export function Field({ label, children, className = "", hint }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

export function Section({ title, icon: Icon, children, right, testId, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-4 space-y-3 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-orange-700" />} {title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

export function Check({ label, checked, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-orange-600" data-testid={testId} />
      {label}
    </label>
  );
}

export function Btn({ children, onClick, tone = "orange", busy, disabled, icon: Icon, testId, className = "", title, type = "button" }) {
  const tones = {
    orange: "bg-orange-600 hover:bg-orange-700 text-white",
    emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
    sky: "bg-sky-600 hover:bg-sky-700 text-white",
    rose: "bg-rose-600 hover:bg-rose-700 text-white",
    slate: "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50",
    ghost: "text-slate-600 hover:bg-slate-100",
  };
  return (
    <button type={type} onClick={onClick} disabled={busy || disabled} title={title}
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${tones[tone]} ${className}`}
      data-testid={testId}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

export const tileHeaderCls = "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3";
export const thCls = "px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500";
export const tdCls = "px-3 py-2 text-sm text-slate-700";
export const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);
export const err = (e, fallback = "Erreur") => toast.error(e?.response?.data?.detail || fallback);
